import { forwardRef } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';

import { cn } from '@/lib/cn';

/**
 * SegmentedControl — pill-style single-select. Built on Radix ToggleGroup
 * (type=single) so a11y + keyboard navigation come for free. Use for
 * mutually-exclusive views: "Day / Week / Month", "All / Active / Archived",
 * "NL / BE / DE".
 *
 * Multi-select use cases should use Tabs (different semantic) or a
 * checkbox group — not this primitive.
 */
type ToggleGroupSingleProps = React.ComponentPropsWithoutRef<typeof ToggleGroup.Root> & {
  type: 'single';
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

export interface SegmentedControlProps
  extends Omit<ToggleGroupSingleProps, 'type'> {
  size?: 'sm' | 'md';
}

export const SegmentedControl = forwardRef<
  React.ElementRef<typeof ToggleGroup.Root>,
  SegmentedControlProps
>(({ className, size: _size = 'md', ...props }, ref) => (
  <ToggleGroup.Root
    ref={ref}
    type="single"
    className={cn(
      'inline-flex items-center gap-xs',
      'bg-bg-tertiary border border-border-subtle rounded-md p-xs',
      className,
    )}
    {...props}
  />
));
SegmentedControl.displayName = 'SegmentedControl';

export interface SegmentedControlItemProps
  extends React.ComponentPropsWithoutRef<typeof ToggleGroup.Item> {
  size?: 'sm' | 'md';
}

export const SegmentedControlItem = forwardRef<
  React.ElementRef<typeof ToggleGroup.Item>,
  SegmentedControlItemProps
>(({ className, size = 'md', ...props }, ref) => (
  <ToggleGroup.Item
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center rounded-sm whitespace-nowrap',
      'font-medium text-text-secondary',
      'transition-colors duration-fast',
      'hover:text-text-primary',
      'data-[state=on]:bg-bg-primary data-[state=on]:text-text-primary',
      'data-[state=on]:border data-[state=on]:border-border',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      size === 'sm' ? 'h-7 px-md text-caption' : 'h-9 px-lg text-body-sm',
      className,
    )}
    {...props}
  />
));
SegmentedControlItem.displayName = 'SegmentedControlItem';
